// freeNadLifeOffer.js (mixin)
// The free NAD+ line a lander's offer link puts on a GLP-1 checkout: the free NAD+
// Nasal or the free NAD+ Injection. The rules live in utils/freeNadLifeOffer.js.
//
// A checkout uses it like this:
//
//   mixins: [freeNadLifeOffer],
//   computed: {
//       // the GLP-1 being bought and its plan length
//       offerGlpProductId() { ... },
//       offerGlpTerms() { ... },
//   }
//
// then sends `orderCartItems` as cartItems and `nadOfferSlug` as `offer` on every
// payment path (card, wallet, Klarna), and renders the line off `offerNadLine`. The line
// is $0, so no total changes.
//
// Rebuilt from the URL on every load: the link is the only statement that this order
// is on the offer. If the offer is not running or the catalog cannot be read, no line
// is shown and none is sent, so the order is a plain GLP-1 order.
//
// The shopper cannot take the line out (Ale, 2026-10-27): the free NAD+ is what the
// link promised them, so no checkout renders a Remove for it.
//
// One NAD+ per order (Ale, 2026-10-27): while the offer's line is in the order, any
// other NAD+ in the saved cart (one added on a product page, say) is taken out of it.

import { fetchProductsFromServer } from '@/utils/productData.js'
import {
    findOfferProduct,
    freeNadLifeLine,
    nadProductIds,
    offerForLink,
    offerLineImage,
    offerLineLabel,
    offerLineName,
    offerLineNote,
    offerLinkRequest
} from '@/utils/freeNadLifeOffer.js'

// Joins the last two words with a no-break space, so a line never ends on one word alone.
// Built from its code so no editor can swap it for a plain space.
const NO_BREAK_SPACE = String.fromCharCode(160)
const keepLastTwoWords = (text) => (text ? text.replace(/ (\S+)$/, `${NO_BREAK_SPACE}$1`) : '')

export default {
    data() {
        return {
            // The /api/checkout-offers record and the product it gives away.
            nadOfferRecord: null,
            nadFreeProduct: null,
            // Every NAD+ product id (an array: a Set is not reactive in Vue 2).
            nadIds: []
        }
    },
    created() {
        this.loadNadOffer()
    },
    watch: {
        offerNadLine: 'dropCartNad',
        cartItems: 'dropCartNad'
    },
    computed: {
        nadLinkRequest() {
            return offerLinkRequest(this.$route.query)
        },
        // The offer's free line for this GLP-1 and plan length.
        nadFreeLine() {
            return freeNadLifeLine({
                offer: this.nadOfferRecord,
                product: this.nadFreeProduct,
                glpProductId: this.offerGlpProductId,
                glpTerms: this.offerGlpTerms
            })
        },
        // The NAD+ line in the order, or null (no offer link, monthly plan).
        offerNadLine() {
            return this.nadLinkRequest ? this.nadFreeLine : null
        },
        // The order lines sent to checkout: the cart, less any other NAD+, plus the line.
        orderCartItems() {
            if (!this.offerNadLine) return this.cartItems
            return [...this.cartItems.filter((item) => !this.nadIds.includes(item.id)), this.offerNadLine]
        },
        // The `offer` slug, sent only with its line: the offer that line is free under.
        nadOfferSlug() {
            return this.offerNadLine ? this.offerNadLine.free_for_life : ''
        },
        offerNadName() {
            return offerLineName(this.offerNadLine)
        },
        offerNadLabel() {
            return keepLastTwoWords(offerLineLabel(this.offerNadLine))
        },
        offerNadNote() {
            return keepLastTwoWords(offerLineNote(this.offerNadLine))
        },
        // The vial or the bottle beside the line.
        nadFreeImage() {
            return offerLineImage(this.nadFreeLine)
        }
    },
    methods: {
        async loadNadOffer() {
            const request = offerLinkRequest(this.$route.query)
            if (!request) return
            try {
                const [offers, products] = await Promise.all([
                    this.$axios.get(`${this.baseUrl}/api/checkout-offers`),
                    fetchProductsFromServer()
                ])
                const offer = offerForLink(offers.data, request)
                const product = findOfferProduct(products, offer)
                if (!offer || !product) return
                this.nadIds = [...nadProductIds(products)]
                this.nadFreeProduct = product
                this.nadOfferRecord = offer
            } catch (error) {
                // Only ever hides the NAD+ line; the GLP-1 order itself is untouched.
                console.error('NAD+ offer could not be loaded:', error)
            }
        },
        // Takes any other NAD+ out of the saved cart while the offer's line is in the order.
        dropCartNad() {
            if (!this.offerNadLine || !this.nadIds.length) return
            this.cartItems
                .filter((item) => this.nadIds.includes(item.id))
                .forEach((item) => this.$store.dispatch('removeFromCart', item.id))
        }
    }
}
